import React, { useEffect, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../styles/Service.css"; 
import Contact from "../Home/Contact";

// Asset Imports
import event from "../../assets/event.webp";
import evento2 from "../../assets/evento2.webp";
import event_left from "../../assets/event_left.webp";
import event_right from "../../assets/event_right.webp";
import nuvexl from "../../assets/nuvexl.webp";
import nuvexr from "../../assets/nuvexl.png";
import nuvex from "../../assets/nuvex.webp";
import nuvex2 from "../../assets/nuvex2.webp";
import iuni_left from "../../assets/iuni_left.webp";
import iuni from "../../assets/iuni.webp";
import iuni2 from "../../assets/iuni2.webp";
import travel from "../../assets/travel.webp";
import travel2 from "../../assets/travel2.webp";
import travel_left from "../../assets/travel_left.webp";
import travel_right from "../../assets/travel_right.webp";
import iuni_right from "../../assets/iuni_right.webp";

// ── 1. Import Sound Controller & Audio Assets ──
import { sounds } from "../Utils/soundEffects";
import buttonClickSfx from "../../assets/sounds/tap-main.mp3";
import panelHoverSfx from "../../assets/sounds/skill-hover.mp3";

gsap.registerPlugin(ScrollTrigger);

const UIUX_CASE_STUDIES = {
travel: {
  title: "Wayfare Travel App",
  subtitle: "Wayfare is a travel planning application designed to help travelers organize every aspect of their journey in one place. From building personalized itineraries and accessing essential details offline to receiving real-time travel updates, the app focuses on making complex trips easier to navigate, manage, and understand.",
  ctaLink: "https://www.behance.net/gallery/218533541/Wayfare-Travel-App-UX-case-study",
  metaInfo: [
    { label: "Timeline", value: "Dec 2024" },
    { label: "Duration", value: "3 Weeks" },
    { label: "Software", value: "Figma | Photoshop" }
  ],
  panels: [
    {
      heading: "THE PROBLEM STATEMENT",
      text: "Travelers often deal with fragmented itinerary views, limited offline access, and overwhelming real-time notifications. Important details like tickets, maps, schedules, and flight updates can easily get buried across different tools."
    },
    {
      heading: "THE PROCESS",
      text: "Researched common travel planning pain points, used card sorting to organize features, and mapped the user journey to understand how travelers move from planning a trip to navigating it in real time."
    },
    {
      heading: "THE SOLUTION ",
      text: "Designed a more connected travel experience around a swipeable journey map, offline access, and customizable notifications, giving travelers clearer control over their itinerary and important updates."
    }
  ],
  mainImage1: travel,
  columnRow: [
    { heading: "Information Architecture", text: "Organized itineraries, bookings, destinations, tickets, maps, and travel updates into a clearer hierarchy so users can find what they need without digging through multiple sections." },
    { heading: "Card Sorting", text: "Used card sorting to understand how different travel features should be grouped and prioritized, helping shape a navigation structure that feels natural rather than simply packing every feature into one place." },
    { heading: "User Journey", text: "Mapped the experience across the full travel journey — from planning and organizing a trip to navigating individual segments, accessing information offline, and responding to real-time updates." }
  ],
  sideImageLeft: travel_left,
  sideImageRight: travel_right,
  mainImage2: travel2,
  footerCaption: "Wayfare challenged me to think beyond individual screens and design around the way people actually experience a trip. By connecting itinerary planning, offline access, and real-time updates into one flow, the project helped me explore how thoughtful UX can make complex travel information feel much easier to manage."
},

bakery: {
  title: "Web UI Design System",
  subtitle: "This project explores a modern e-commerce experience for lifestyle products, designed around a minimal interface, premium branding, and a smoother shopping experience. Designed in Figma and developed using ReactJS and CSS, the project focuses on creating a refined visual system, responsive layouts, and engaging product presentation.",
  ctaLink: "http",
  metaInfo: [
    { label: "Timeline", value: "Dec 2025" },
    { label: "Duration", value: "1.5 Weeks" },
    { label: "Software", value: "Figma | ReactJS | CSS" }
  ],
  panels: [
    {
      heading: "DESIGN FOCUS",
      text: "Created a modern e-commerce experience for lifestyle products with a focus on simplicity, usability, premium branding, and a polished visual presence."
    },
    {
      heading: "VISUAL SYSTEM",
      text: "Built a minimal design system around clean layouts, bold typography, neutral tones, and bright orange accents. The color palette was intentionally chosen to give the brand a premium feel while keeping the interface approachable and easy to navigate."
    },
    {
      heading: "FINAL RESULT",
      text: "A refined and responsive shopping experience that balances premium branding with practical usability, giving lifestyle products room to stand out without making the interface feel overwhelming."
    }
  ],
  mainImage1: iuni,
  columnRow: [
    {
      heading: "Product Hierarchy",
      text: "Structured product information, imagery, pricing, and key details with a clear visual hierarchy so users can quickly understand what they are looking at and move naturally through the shopping experience."
    },
    {
      heading: "Responsive Layouts",
      text: "Designed flexible layouts that adapt across screen sizes while preserving spacing, hierarchy, and the overall visual character of the interface."
    },
    {
      heading: "Interactive Details",
      text: "Added considered hover states, transitions, buttons, and interactive elements to make the interface feel responsive and polished rather than like a collection of static screens."
    }
  ],
  sideImageLeft: iuni_left,
  sideImageRight: iuni_right,
  mainImage2: iuni2,
  footerCaption: "The project was an exercise in making a lifestyle e-commerce experience feel premium without overcomplicating it. Every part of the interface — from the minimal visual system and color palette to the product presentation and responsive layouts — was designed to keep the experience clean, modern, and easy to explore."
}, 
saas: {
  title: "Fintech SaaS Website",
  subtitle: "This project involved designing and developing a modern fintech SaaS website for NUVEX, focused on a clean visual system, clear financial data presentation, and a polished user experience. The website was designed in Figma and developed in Next.js with a fully responsive frontend.",
  ctaLink: "https://nuvex-iota.vercel.app/",
  metaInfo: [
    { label: "Timeline", value: "August 2026" },
    { label: "Duration", value: "2 Days" },
    { label: "Software", value: "Figma | Next.js" }
  ],
  panels: [
    { heading: "PROJECT SCOPE",
      text: "UI/UX, visual design, branding, responsive design, interaction design, and frontend development."},
    { heading: "DESIGN TO CODE",
      text: "Explored the visual direction first, then translated the designs into a responsive Next.js frontend — building the interface, interactions, transitions, and reusable components directly in code."},
    { heading: "FINAL EXPERIENCE",
      text: "A polished, responsive SaaS experience that doesn’t feel like a static website. The project brings the visual quality of the design into code while leaving room to grow into richer pages, dashboards, and data-driven experiences."}
  ],
  mainImage1: nuvex,
  columnRow: [
    { heading: "UI System",
      text: "Built a cohesive visual system across typography, color, cards, layouts, and data-focused components, giving the product a consistent SaaS aesthetic that can scale as more pages are added."
    },
    { heading: "Interactions & Motion",
     text: "Used transitions, hover states, scroll-based movement, and subtle animations to give the interface a sense of depth and responsiveness — showing that a coded website doesn’t have to feel flat or static."},
    { heading: "Design to Code",
      text: "Translated the Figma designs into a responsive Next.js frontend, paying attention to the small details between design and implementation so the final experience stays close to the original vision while remaining flexible to build on." }
  ],
  sideImageLeft: nuvexl,
  sideImageRight: nuvexr,
  mainImage2: nuvex2,
  footerCaption: "This project let me explore what happens when a polished UI design is taken beyond the canvas and brought to life with code. It strengthened my approach to building interactive, responsive experiences and gave me a foundation to expand the product into richer pages, dashboards, and more complex data visualizations."

},
event: {
  title: "Evento Event Planner App",
  subtitle: "Evento is a mobile event management application designed to simplify every stage of planning, from creating events and managing budgets to coordinating teams and tracking guests. The project focuses on reducing fragmented workflows by bringing every essential task into one intuitive, user-centered experience.",
  ctaLink: "https://www.behance.net/gallery/232094487/Evento-Event-Planner-App-UX-Case-Study",
  metaInfo: [
    { label: "Timeline", value: "June 2025" },
    { label: "Duration", value: "4 Weeks" },
    { label: "Software", value: "Figma | Notion" }
  ],
  panels: [
    { heading: "WHAT WAS THE CHALLENGE",
      text: "Most event organizers deal with scattered workflows, overwhelming interfaces, and disconnected tools for managing teams, budgets, guests, and schedules. The challenge was to bring these workflows together without making the experience feel equally overwhelming."},
    { heading: "HOW I MADE THE DECISIONS",
      text: "Focused on identifying which features were essential, how they should connect, and where complexity could be reduced. User flows, information architecture, and card sorting helped shape a structure that felt comprehensive without becoming unnecessarily complicated."},
    { heading: "WHAT THE UX BECAME",
      text: "The resulting concept brings core planning workflows into one centralized experience, giving organizers clearer visibility across budgets, guests, teams, and event tasks while creating a more structured foundation for collaboration and real-time updates."}
  ],

  mainImage1: event,

  columnRow: [

    {
      heading: "User Flows",
      text: "Mapped the key journeys across event creation, budgeting, guest management, team coordination, and other core tasks to understand how users would move through the product from one action to the next."
    },

    {
      heading: "Information Architecture",
      text: "Organized the app around the way event planners actually think about their work, grouping related information and actions into clear sections so users could find what they needed without navigating through unnecessary layers."
    },

    {
      heading: "Feature Prioritization",
      text: "Used card sorting and feature evaluation to decide what deserved to be prominent, what could be combined, and what could stay secondary. The goal was to find the balance between an app that felt useful and one that felt overwhelming."
    }

  ],

  sideImageLeft: event_left,

  sideImageRight: event_right,

  mainImage2: evento2,

  footerCaption: "Designing Evento pushed me to think beyond individual screens and focus on how the entire product fits together. From mapping user journeys to deciding how much information users should see at each stage, the project strengthened my approach to designing complex experiences that still feel approachable."

},
};

export default function UIUXPage({ project, onBack }) {
  const data = UIUX_CASE_STUDIES[project] || UIUX_CASE_STUDIES.travel;
  const [activePanelIdx, setActivePanelIdx] = useState(null);

  // ── 2. Preload Sounds on Mount ──
  useEffect(() => {
    sounds.loadSound("uiuxButtonClick", buttonClickSfx);
    sounds.loadSound("uiuxPanelHover", panelHoverSfx);

    const unlockAudio = () => {
      sounds.init();
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };

    window.addEventListener("pointerdown", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    let ctx = gsap.context(() => {
      gsap.fromTo(".uiux-back-btn", 
        { opacity: 0, x: -15 },
        { opacity: 1, x: 0, duration: 0.5, ease: "power2.out" }
      );

      gsap.fromTo(".case-split-header > *",
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", stagger: 0.12 }
      );

      gsap.fromTo(".case-panels-row .expandable-case-panel",
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.08, ease: "power2.out" },
        "-=0.3"
      );

      const revealElements = document.querySelectorAll(".scroll-reveal");
      revealElements.forEach((element) => {
        const targets = element.classList.contains("uiux-metrics-row") 
          ? element.querySelectorAll(".uiux-column") 
          : element;

        gsap.fromTo(targets,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power2.out",
            stagger: 0.15,
            scrollTrigger: {
              trigger: element,
              start: "top 85%",
              toggleActions: "play none none none"
            }
          }
        );
      });
    });

    return () => {
      ctx.revert();
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [project]);

  // Click handlers with audio feedback
  const handleBackClick = () => {
    sounds.play("uiuxButtonClick", 0.35);
    if (onBack) onBack();
  };

  const handleCtaClick = () => {
    sounds.play("uiuxButtonClick", 0.35);
  };

  const handlePanelToggle = (idx, isOpen) => {
    sounds.play("uiuxButtonClick", 0.25);
    setActivePanelIdx(isOpen ? null : idx);
  };

  return (
    <div className="uiux-detail-wrapper">
      <button className="uiux-back-btn" onClick={handleBackClick} aria-label="Go Back">
        <span className="arrow">←</span>
      </button>

      {/* ── 1. Split Header ── */}
      <div className="case-split-header">
        <div className="case-title-col">
          <h1>{data.title}</h1>
        </div>

        <div className="case-desc-col">
          <p>{data.subtitle}</p>
          <a href={data.ctaLink} target="_blank" rel="noreferrer" className="uiux-project-cta" onClick={handleCtaClick}>
            Explore
          </a>
        </div>
      </div>

      {/* ── 2. Metadata Bar ── */}
      {data.metaInfo && (
        <div className="uiux-meta-bar">
          {data.metaInfo.map((item, idx) => (
            <span key={idx} className="meta-item-group">
              <strong className="meta-label">{item.label}: </strong>
              <span className="meta-value">{item.value}</span>
            </span>
          ))}
        </div>
      )}

      {/* ── 3. Upper Expandable Panels ── */}
      <div className="case-panels-row">
        {data.panels.map((panel, idx) => {
          const isOpen = activePanelIdx === idx;
          return (
            <div
              key={idx}
              className={`expandable-case-panel ${isOpen ? "is-open" : ""}`}
              onMouseEnter={() => sounds.play("uiuxPanelHover", 0.2)}
              onClick={() => handlePanelToggle(idx, isOpen)}
            >
              <div className="panel-header-bar">
                <span className="panel-title">{panel.heading}</span>
                <svg
                  className="panel-arrow-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="19" x2="19" y2="5" />
                  <polyline points="7 5 19 5 19 17" />
                </svg>
              </div>
              <div className="panel-drawer">
                <div className="panel-drawer-inner">
                  <p>{panel.text}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── 4. Main Media Showcase 1 ── */}
      <div className="uiux-media-container width-80 scroll-reveal">
        <img src={data.mainImage1} alt={`${data.title} Cover`} className="uiux-fluid-img" />
      </div>

      {/* ── 5. Three-Column Technical Specifications / Metrics Row ── */}
      <div className="uiux-metrics-row scroll-reveal">
        {data.columnRow.map((col, index) => (
          <div className="uiux-column" key={index}>
            <h2>{col.heading}</h2>
            <p>{col.text}</p>
          </div>
        ))}
      </div>

      {/* ── 6. Split Dual Media Row ── */}
      <div className="uiux-split-media-row scroll-reveal">
        <div className="uiux-media-container width-40">
          <img src={data.sideImageLeft} alt="Detail view left" className="uiux-fluid-img" />
        </div>
        <div className="uiux-media-container width-40">
          <img src={data.sideImageRight} alt="Detail view right" className="uiux-fluid-img" />
        </div>
      </div>

      {/* ── 7. Main Media Showcase 2 ── */}
      <div className="uiux-media-container width-80 scroll-reveal">
        <img src={data.mainImage2} alt="Product breakdown stream" className="uiux-fluid-img" />
      </div>

      {/* ── 8. Footer Summary Caption ── */}
      <div className="uiux-footer-caption scroll-reveal">
        <p>{data.footerCaption}</p>
      </div>

      <Contact />
    </div>
  );
}